/**
 * Contact Management API Service
 * Handles Customer and Supplier CRUD operations with proper error handling
 */

import config from "../../config";
import { apiFetch, createMultiConsumerGet } from "./apiFetch";


const getAccountId = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user.accountId;
};

const buildUrl = (path, params = {}) => {
    const filtered = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
    );
    const qs = new URLSearchParams({ account_id: getAccountId(), ...filtered });
    return `${config.apiUrl}/${path}?${qs}`;
};

const sharedGet = createMultiConsumerGet(async (url, options) => {
    const res = await apiFetch(url, options);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
});
const get = (path, params = {}, options = {}) => {
    const url = buildUrl(path, params);
    return sharedGet(url, options);
};

// ════════════════════════════════════════════════════════════
// CUSTOMERS API
// ════════════════════════════════════════════════════════════

export const getCustomers = (p = {}, options = {}) =>
    get("getCustomers", p, options);

export const getCustomerKpis = () => get("getCustomerKpis");

export const getCustomerById = (id, options = {}) => get("getCustomerById", { id }, options);

export const getCustomerCompanyNames = async (options = {}) => {
    const { data } = await get("getCustomers", { pageSize: 1000, page: 1 }, options);
    return [...new Set((data || []).map((r) => r.name).filter(Boolean))].sort();
};

export const createCustomer = async (data) => {
    const res = await apiFetch(`${config.apiUrl}/createCustomer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: getAccountId(), ...data }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const updateCustomer = async (id, data) => {
    const res = await apiFetch(`${config.apiUrl}/updateCustomer`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: getAccountId(), id, ...data }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const deleteCustomer = async (id) => {
    const res = await apiFetch(buildUrl("deleteCustomer", { id }), { method: "DELETE" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

// ════════════════════════════════════════════════════════════
// SUPPLIERS API
// ════════════════════════════════════════════════════════════

export const getSuppliers = (p = {}, options = {}) =>
    get("getSuppliers", p, options);

export const getSupplierKpis = () => get("getSupplierKpis");

export const getSupplierById = (id, options = {}) => get("getSupplierById", { id }, options);

export const getSupplierCompanyNames = async (options = {}) => {
    const { data } = await get("getSuppliers", { pageSize: 1000, page: 1 }, options);
    return [...new Set((data || []).map((r) => r.name).filter(Boolean))].sort();
};

export const createSupplier = async (data) => {
    const res = await apiFetch(`${config.apiUrl}/createSupplier`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: getAccountId(), ...data }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const updateSupplier = async (id, data) => {
    const res = await apiFetch(`${config.apiUrl}/updateSupplier`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: getAccountId(), id, ...data }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const deleteSupplier = async (id) => {
    const res = await apiFetch(buildUrl("deleteSupplier", { id }), { method: "DELETE" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

// ════════════════════════════════════════════════════════════
// CONSTANTS
// ════════════════════════════════════════════════════════════

export const INCOTERMS_LIST = [
    "EXW – Ex Works",
    "FCA – Free Carrier",
    "FAS – Free Alongside Ship",
    "FOB – Free On Board",
    "CFR – Cost and Freight",
    "CIF – Cost, Insurance and Freight",
    "CPT – Carriage Paid To",
    "CIP – Carriage and Insurance Paid To",
    "DAP – Delivered At Place",
    "DPU – Delivered at Place Unloaded",
    "DDP – Delivered Duty Paid",
];

export const COMMODITY_LIST = [
    "Aluminium", "Steel", "Copper", "Plastics", "ABS Resin",
    "PCB", "Electronics", "Cables", "Rubber", "Gaskets",
    "Bearings", "Fasteners", "Paint", "Chemicals", "Solvents",
    "Timber", "Glass", "Packaging", "Cartons", "Lubricants",
    "Sensors", "Valves", "Adhesives", "Welding Gas", "Logistics",
];

export const AREAS = [];
export const STATUSES = ["All", "Active", "Inactive"];
export const SEARCH_TYPES = ["all", "id", "name", "contact", "email", "phone", "area"];